import React, { Component } from "react";
import { Modal, Button } from "antd";
import { CloseOutlined, PlayCircleOutlined } from "@ant-design/icons";

// ── VIDEO CONFIG ──────────────────────────────────────────────────────────────
// Replace these YouTube embed URLs with your actual video IDs when ready.
// Format: https://www.youtube.com/embed/VIDEO_ID?autoplay=1&rel=0
// To get embed URL: YouTube → Share → Embed → copy the src value
//
// If you don't have videos yet, set the url to null and a "coming soon" 
// placeholder shows instead — modal still fires so users see the message.

const VIDEOS = {
  // Consumer profiles
  B:  {
    title:    "Welcome, Home Buyer! 🏡",
    subtitle: "Here's everything My Home Buddha can do for you",
    url:      "https://www.youtube.com/embed/I3FiWeDsmUc?si=fmKlQjfpCXsPK0Tm",
    duration: "3 min",
    bullets: [
      "🔍 Search MLS and Off-MLS properties with AI analysis",
      "📊 Get instant property valuations and inspection checklists",
      "💰 Check your buying readiness and estimated buying power",
      "🤝 Connect with trusted agents and lenders in your area",
      "📋 Save and compare properties side by side",
    ],
  },
  S:  {
    title:    "Welcome, Home Seller! 🏠",
    subtitle: "Here's how My Home Buddha helps you sell smarter",
    url:      "https://www.youtube.com/embed/I3FiWeDsmUc?si=fmKlQjfpCXsPK0Tm",
    duration: "3 min",
    bullets: [
      "💰 Get an AI-powered home valuation in seconds",
      "📋 Build a Certified Home Facts Report for buyers",
      "🔧 See exactly what to fix before listing for max ROI",
      "📈 Understand your market with comparable sales data",
      "🤝 Connect with agents who know your neighborhood",
    ],
  },
  HO: {
    title:    "Welcome, Homeowner! 🏡",
    subtitle: "Your personal home health dashboard is ready",
    url:      "https://www.youtube.com/embed/I3FiWeDsmUc?si=fmKlQjfpCXsPK0Tm",
    duration: "3 min",
    bullets: [
      "🏥 Take the Home Health Check-Up — see how your home scores",
      "📷 Upload photos of your systems for AI age and condition analysis",
      "🔧 Track your roof, HVAC, plumbing, electrical and more",
      "💰 Monitor your home's value and build equity strategically",
      "🤝 Connect with pros for quotes and maintenance services",
    ],
  },
  // Professional profiles
  AG: {
    title:    "Welcome, Agent! ⭐",
    subtitle: "Your AI-powered client toolkit is ready",
    url:      "https://www.youtube.com/embed/WJPvofeOd9g?si=EZPOnbr0is3tIaM8",
    duration: "4 min",
    bullets: [
      "🎨 Set up your agent branding — logo, photo, colors, bio",
      "🗺️ Prospect neighborhoods with satellite + data overlay maps",
      "📊 Share AI property reports directly with clients",
      "✍️ Generate social posts, listing copy, and market updates",
      "🏡 Invite homeowners into their Home Buddha portal",
      "📈 Track your network of buyers, sellers, and homeowners",
    ],
  },
  L:  {
    title:    "Welcome, Lending Partner! 💼",
    subtitle: "Here's how My Home Buddha connects you with clients",
    url:      "https://www.youtube.com/embed/WJPvofeOd9g?si=EZPOnbr0is3tIaM8",
    duration: "3 min",
    bullets: [
      "🎨 Set up your lender branding across all AI tools",
      "🏠 Appear in the sponsor strip on buyer and homeowner pages",
      "💰 Connect with buyers checking their buying readiness",
      "📊 Access property data and client reports",
      "🤝 Build your referral network with agents on the platform",
    ],
  },
  V:  {
    title:    "Welcome, Vendor! 🔧",
    subtitle: "Here's how My Home Buddha connects you with homeowners",
    url:      "https://www.youtube.com/embed/yzjXXkLNeHQ?si=aTrVSC3XGlnoFw_D",
    duration: "2 min",
    bullets: [
      "🎨 Set up your vendor profile and service areas",
      "🏡 Connect with homeowners who need your services",
      "📊 Appear in the Home Buddha portal service recommendations",
      "⭐ Build reviews and trust through the platform",
      "🤝 Grow your referral network with agents and lenders",
    ],
  },
};

// Fallback for unknown profile types
const DEFAULT_VIDEO = {
  title:    "Welcome to My Home Buddha! 🏠",
  subtitle: "Healthy · Wealthy · Wise — your home, managed smarter",
  url:      "https://www.youtube.com/embed/I3FiWeDsmUc?si=fmKlQjfpCXsPK0Tm",
  duration: "3 min",
  bullets: [
    "🔍 AI-powered property research and valuations",
    "🏡 Home health check-up and portal for every homeowner",
    "⭐ Agent and lender branding tools",
    "📊 Buyer readiness calculator and buying power estimate",
    "🤝 Connect with trusted professionals in your area",
  ],
};

const NAVY = "#1A2280";
const GOLD = "#D4921A";
const STORAGE_KEY = "mhb_welcome_seen_v1"; // bump suffix to re-show for all users

class WelcomeVideoModal extends Component {
  state = { show: false, playing: false, profile: null };

  componentDidMount() {
    // Small delay so the page settles before the modal fires
    setTimeout(this.checkShouldShow, 2000);
  }

  componentDidUpdate(prevProps) {
    // Re-check when user logs in (userDetails becomes available)
    const prev = prevProps.context?.state?.userDetails;
    const curr = this.props.context?.state?.userDetails;
    if (!prev && curr) {
      setTimeout(this.checkShouldShow, 1000);
    }
  }

  checkShouldShow = () => {
    try {
      const ud = this.props.context?.state?.userDetails
        || JSON.parse(localStorage.getItem("userDetails") || "null");
      if (!ud || !ud.id) return; // not logged in

      const profile = ud.userProfile || "B";
      const seenKey = STORAGE_KEY + "_" + ud.id;
      const seen    = localStorage.getItem(seenKey);

      if (!seen) {
        this.setState({ show: true, profile });
      }
    } catch(e) {
      console.warn("[WelcomeVideo] error:", e);
    }
  };

  handleClose = () => {
    try {
      const ud = this.props.context?.state?.userDetails
        || JSON.parse(localStorage.getItem("userDetails") || "null");
      if (ud?.id) {
        localStorage.setItem(STORAGE_KEY + "_" + ud.id, "1");
      }
    } catch(e) {}
    this.setState({ show: false, playing: false });
  };

  handlePlay = () => this.setState({ playing: true });

  render() {
    const { show, playing, profile } = this.state;
    if (!show) return null;

    const video = VIDEOS[profile] || DEFAULT_VIDEO;

    return (
      <Modal
        visible={show}
        onCancel={this.handleClose}
        footer={null}
        width={620}
        style={{ top: 30 }}
        bodyStyle={{ padding: 0 }}
        className="modal-black-close"
        closeIcon={<CloseOutlined style={{ color: "white", fontSize: "16px" }} />}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`,
          padding: "24px 28px 20px",
          borderRadius: "8px 8px 0 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <span style={{ fontSize: "28px" }}>🏠</span>
            <div>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: "11px", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: "2px" }}>
                My Home Buddha
              </div>
              <div style={{ color: "white", fontWeight: 800, fontSize: "18px", lineHeight: 1.2 }}>
                {video.title}
              </div>
            </div>
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", marginTop: "4px" }}>
            {video.subtitle}
          </div>
        </div>

        <div style={{ padding: "0 24px 24px" }}>

          {/* Video area */}
          <div style={{
            margin: "20px 0 16px",
            borderRadius: "12px",
            overflow: "hidden",
            background: "#0a0e2e",
            position: "relative",
            paddingBottom: "56.25%", // 16:9 ratio
            height: 0,
          }}>
            {video.url && playing ? (
              <iframe
                src={video.url}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: "absolute", top: 0, left: 0,
                  width: "100%", height: "100%",
                }}
              />
            ) : (
              /* Placeholder / play button */
              <div style={{
                position: "absolute", top: 0, left: 0,
                width: "100%", height: "100%",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg,#0a0e2e,#1A2280)",
                cursor: video.url ? "pointer" : "default",
              }} onClick={video.url ? this.handlePlay : undefined}>
                {video.url ? (
                  <>
                    <PlayCircleOutlined style={{ fontSize: "64px", color: GOLD, marginBottom: "12px" }} />
                    <div style={{ color: "white", fontWeight: 700, fontSize: "14px" }}>
                      Watch the intro — {video.duration}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", marginTop: "4px" }}>
                      Click to play
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: "48px", marginBottom: "10px" }}>🎬</div>
                    <div style={{ color: "white", fontWeight: 700, fontSize: "14px", marginBottom: "6px" }}>
                      Intro video coming soon
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px", textAlign: "center", maxWidth: "280px", lineHeight: 1.6 }}>
                      We're putting the finishing touches on your personalized walkthrough. Check back soon!
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* What's available for this user type */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "12px", fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "10px" }}>
              Here's what's waiting for you
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {video.bullets.map((b, i) => (
                <div key={i} style={{
                  fontSize: "13px", color: "#2D3748", lineHeight: 1.5,
                  padding: "7px 12px",
                  background: i % 2 === 0 ? "#F7F9FC" : "white",
                  borderRadius: "8px",
                }}>
                  {b}
                </div>
              ))}
            </div>
          </div>

          {/* Footer buttons */}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button onClick={this.handleClose}
              style={{ borderRadius: "50px", borderColor: "#E2E8F0", color: "#718096" }}>
              Skip for now
            </Button>
            {video.url && !playing && (
              <Button type="primary" icon={<PlayCircleOutlined />} onClick={this.handlePlay}
                style={{ borderRadius: "50px", background: GOLD, borderColor: GOLD, color: "#2A1600", fontWeight: 700 }}>
                Watch Intro ({video.duration})
              </Button>
            )}
            <Button type="primary" onClick={this.handleClose}
              style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY, fontWeight: 700 }}>
              Let&apos;s explore! 🚀
            </Button>
          </div>
        </div>

      </Modal>
    );
  }
}

export default WelcomeVideoModal;